import t from 'react-translate';
import { CellProps } from './types';
import { css } from '@emotion/react';
import { config } from '@config/pendo.config.json';
import MoreOptionsTarget from 'athena/components/more-options-target';
import {
  NvDropdown,
  NvDropdownButtonStyle,
  NvDropdownOption,
} from 'shared/components/inputs/nv-dropdown';
import { useState } from 'react';
import { useAppDispatch } from 'redux/store';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { wrapThunkAction } from 'redux/utils';
import { deleteCustomCommunication } from 'redux/actions/mentorship-program-custom-communications';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { ModalMode } from '../index';

const styles = css`
  .options-target {
    opacity: 0;
    flex-shrink: 0;
  }
`;

const OptionsCell = ({ cell }: CellProps) => {
  const dispatch = useAppDispatch();
  const {
    row: { original: mentorshipProgramCommunication },
    getContext,
  } = cell;
  const { id: communicationId, mentorshipProgramId: programId } =
    mentorshipProgramCommunication || {};
  const {
    table: {
      options: { meta },
    },
  } = getContext();
  const { reset, setModalState } = meta || {};
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const translationBase = t.MENTORING_PROGRAMS.COMMUNICATIONS.TABLE;
  const dataQATag =
    config.pendo.athena.mentorshipProgram.communications
      .communicationMenuDropdown;

  const options: NvDropdownOption[] = [
    {
      text: translationBase.OPTIONS.VIEW(),
      type: 'text',
      callback: () => setModalState({ mode: ModalMode.VIEW, communicationId }),
      dataQa: dataQATag.items.view,
    },
    {
      text: translationBase.OPTIONS.EDIT(),
      type: 'text',
      callback: () => setModalState({ mode: ModalMode.EDIT, communicationId }),
      dataQa: dataQATag.items.edit,
    },
    { type: 'divider' },
    {
      text: translationBase.OPTIONS.MAKE_A_COPY(),
      type: 'text',
      callback: () => setModalState({ mode: ModalMode.COPY, communicationId }),
      dataQa: dataQATag.items.makeACopy,
    },
    {
      text: translationBase.OPTIONS.DELETE(),
      type: 'text',
      class: 'text-danger',
      callback: () => onDeleteCustomCommunication(),
      dataQa: dataQATag.items.delete,
    },
  ];

  const onDeleteCustomCommunication = () => {
    dispatch(
      openConfirmationDialog({
        title: translationBase.OPTIONS.DELETE_CONFIRMATION_MODAL.TITLE(),
        bodyText: translationBase.OPTIONS.DELETE_CONFIRMATION_MODAL.BODY(),
        confirmText: translationBase.OPTIONS.DELETE(),
        onConfirm: () => {
          wrapThunkAction(
            dispatch(
              deleteCustomCommunication({
                programId,
                communicationId,
              })
            )
          ).then(() => {
            dispatch(
              addAlertMessage({
                type: AlertMessageType.SUCCESS,
                header: t.FORM.SUCCESS_BANG(),
                message:
                  translationBase.OPTIONS.DELETE_CONFIRMATION_MODAL.DELETION_SUCCESS(),
              })
            );
            reset?.();
          });
        },
        cancelDataQa: dataQATag.deleteCommunicationConfirmation.cancelButton,
        confirmDataQa: dataQATag.deleteCommunicationConfirmation.confirmButton,
      })
    );
    setIsDropdownOpen(false);
  };

  const handleDropdownToggle = () => {
    setIsDropdownOpen((prevState) => !prevState);
  };

  return (
    <div css={styles} className='mr-4' onClick={(e) => e.stopPropagation()}>
      <NvDropdown
        items={options}
        buttonStyle={NvDropdownButtonStyle.CUSTOM}
        customTarget={() => <MoreOptionsTarget className='options-target' />}
        altLabel={translationBase.ARIA_LABEL.COMMUNICATION_OPTIONS_MENU()}
        toggleDataQa={dataQATag.toggle}
        toggleDataQaId={`${dataQATag.toggle}-${communicationId}`}
        onToggle={handleDropdownToggle}
        ariaExpanded={isDropdownOpen}
        ariaRole='menu'
      />
    </div>
  );
};

export default OptionsCell;
